import { RichTextDisplay, RichTextEditor } from '@tempo-io/fim3-sui-shared';
import type { CustomFieldDTO } from '@tempo-io/item-store-dto';
import { Alert, AlertDescription } from '@tempo-io/sui-alert';
import { Button } from '@tempo-io/sui-button';
import {
  AccountBalance,
  BuildCircle,
  Dependency,
  EditNote,
  EventAvailable,
  ExternalLink,
  Settings,
  SyncProblem,
} from '@tempo-io/sui-icons';
import { NavigationItem, PrimaryNavigationSection } from '@tempo-io/sui-navigation';
import { Select } from '@tempo-io/sui-select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@tempo-io/sui-tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@tempo-io/sui-tooltip';
import type { TFunction } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Item } from '@/common/api/types/items';
import type { ItemTypeLevel } from '@/common/api/types/itemType';
import { sortItemTypesByLevel } from '@/common/api/types/itemType';
import type {
  WorkIntegration_Keys,
  WorkIntegrationResponse,
} from '@/common/api/types/workIntegrations';
import { SourceSystemIcon } from '@/common/components/icons/SourceSystemIcon';
import { ParentItemAutocomplete } from '@/common/components/ParentItemAutocomplete';
import type { ParentItemOption } from '@/common/components/parent-item-search/parentItemSearch';
import { ItemSyncButton } from '@/common/components/sync-work-integrations/ItemSyncButton';
import { ItemTypeIcon } from '@/common/components/ui/item-type/itemTypeIcon';
import { ItemStatusBadge } from '@/common/components/ui/itemStatusBadge';
import { ABOVE_RICH_TEXT_EDITOR_MENU_STYLES } from '@/common/constants/richTextEditorZIndex';
import { useSourceSystemLinkLabel } from '@/common/hooks/useSourceSystemLinkLabel';
import { IntegrationField } from '@/features/item-panel/components/IntegrationField';
import { Dependencies } from '@/features/item-panel/components/tabs/dependencies/Dependencies';
import { Financials } from '@/features/item-panel/components/tabs/Financials';
import { TabValue, type TabValueType } from '@/features/item-panel/types';
import { EmptyFieldValue } from '@/features/items/components/item-field-value/EmptyFieldValue';
import { FieldWithStatusIndicator } from '@/features/items/components/item-field-value/FieldWithStatusIndicator';
import { ItemSyncFailures } from '@/features/settings/work-integrations/components/integration-detail/ItemSyncFailures';
import { useGetAllWorkIntegrationsById } from '@/features/settings/work-integrations/queries/useGetAllWorkIntegrations';
import { useGetItemSyncFailedItems } from '@/features/settings/work-integrations/queries/useGetItemSyncFailedItems';
import { useGetWorkIntegrationIdsByItemIds } from '@/features/settings/work-integrations/queries/useGetWorkIntegrationItems';
import { useIsPublishedContext } from '@/features/views/components/published/PublishedContext';
import type { ItemDetailsTabAdapter } from '../adapters/types';
import { TabAdapterProvider, useTabAdapter } from '../context/TabAdapterContext';
import { useItemPanelQueryWithPublished } from '../hooks/useItemPanelQueryWithPublished';
import { useItemTypeLevelsQueryWithPublished } from '../hooks/useItemTypeLevelsQueryWithPublished';
import type { TabConfig } from '../types';
import { CustomFields } from './custom-fields';
import { DESCRIPTION_MAX_LENGTH, descriptionSchema } from './itemDetails.schema';
import { PlanScheduling } from './PlanScheduling';
import { SystemFields } from './SystemFields';

const INTEGRATION_DESCRIPTION_MAX_LENGTH = 20_000;
const EMPTY_ITEM_TYPE_LEVELS: ItemTypeLevel[] = [];

const OptionWithIcon = ({
  option,
}: {
  option: { label: string; icon?: React.ReactNode | null };
}) => (
  <div className="flex items-center gap-2">
    {option.icon}
    <span>{option.label}</span>
  </div>
);

type FieldName = 'description' | 'itemStatusId' | 'parentId' | 'itemTypeId';

type FieldState = {
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: Error | null;
};

type OptimisticFieldValues = {
  itemStatusId: number;
  itemTypeId: number;
};

const syncOptimisticFieldValue = <T,>(localValue: T, sourceValue: T, fieldState: FieldState): T => {
  if (fieldState.isPending) {
    return localValue;
  }

  if (fieldState.isSuccess && localValue !== sourceValue) {
    return localValue;
  }

  return sourceValue;
};

interface ExternalKeyCardProps {
  itemId: number;
  externalKey: string;
  externalLink: string | null | undefined;
  integration: WorkIntegrationResponse | undefined;
  sourceSystem: WorkIntegration_Keys | undefined;
}

const ExternalKeyCard = ({
  itemId,
  externalKey,
  externalLink,
  integration,
  sourceSystem,
}: ExternalKeyCardProps) => {
  const { t } = useTranslation();
  const instanceName = integration?.name || integration?.instanceUrl;
  const linkLabel = useSourceSystemLinkLabel(sourceSystem);
  return (
    <div
      data-testid="external-key-card"
      className="flex flex-col gap-1 rounded-lg border border-border-neutral-secondary p-4"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 text-sm font-bold text-text-neutral-primary break-words">
          {instanceName ?? t(($) => $.adaptivePlanner.itemPanel.systemField.externalKey)}
        </span>
        {integration && (
          <div className="flex shrink-0 items-center gap-2">
            <ItemSyncButton itemId={itemId} integration={integration} />
          </div>
        )}
      </div>
      <div className="flex items-center gap-1.5 text-sm text-text-neutral-secondary">
        {sourceSystem && <SourceSystemIcon sourceSystemType={sourceSystem} />}
        <span>{t(($) => $.adaptivePlanner.itemPanel.systemField.externalKey)}:</span>
        {externalLink ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href={externalLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-text-link hover:underline"
              >
                {externalKey}
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </TooltipTrigger>
            <TooltipContent>{linkLabel}</TooltipContent>
          </Tooltip>
        ) : (
          <span>{externalKey}</span>
        )}
      </div>
    </div>
  );
};

interface DescriptionFieldProps {
  isEditable: boolean;
  isDisabled: boolean;
  description: string | null | undefined;
  truncationLink: string | null;
  fieldState: FieldState;
  itemId: number;
  editorKey: number;
  sourceSystem: WorkIntegration_Keys | undefined;
  onSave: (value: string) => Promise<void>;
}

const DescriptionField = ({
  isEditable,
  isDisabled,
  description,
  truncationLink,
  fieldState,
  itemId,
  editorKey,
  sourceSystem,
  onSave,
}: DescriptionFieldProps) => {
  const { t } = useTranslation();
  const linkLabel = useSourceSystemLinkLabel(sourceSystem);
  return (
    <FieldWithStatusIndicator
      inputId="item-description"
      name={t(($) => $.adaptivePlanner.itemPanel.itemSection.itemDescription)}
      isPending={fieldState.isPending}
      isSuccess={fieldState.isSuccess}
      className="flex-1"
    >
      {!isEditable && truncationLink && (
        <Alert variant="info">
          <AlertDescription>
            <div
              data-testid="description-truncation-notice"
              className="flex items-center justify-between gap-2"
            >
              <span>
                {t(($) => $.adaptivePlanner.itemPanel.itemSection.descriptionTruncatedNotice)}
              </span>
              <Button size="sm" variant="outline" asChild>
                <a href={truncationLink} target="_blank" rel="noopener noreferrer">
                  <ExternalLink />
                  {linkLabel}
                </a>
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}
      {isEditable ? (
        <div className="flex flex-1 min-h-40 flex-col gap-1">
          {fieldState.isError && fieldState.error && (
            <span role="alert" aria-live="polite" className="text-xs text-text-error">
              {fieldState.error.message}
            </span>
          )}
          <RichTextEditor
            id="item-description"
            key={`${itemId}-${editorKey}`}
            defaultValue={description ?? ''}
            onBlur={isDisabled ? undefined : onSave}
            fillHeight
          />
        </div>
      ) : description ? (
        <div className="flex-1 min-h-40 overflow-y-auto">
          <RichTextDisplay value={description} />
        </div>
      ) : (
        <div className="flex-1 min-h-40">
          <EmptyFieldValue />
        </div>
      )}
    </FieldWithStatusIndicator>
  );
};

const SIDEBAR_TAB_IDS = ['system', 'customFields', 'planScheduling'] as const;
export type SidebarTabId = (typeof SIDEBAR_TAB_IDS)[number];
const sidebarTabIds = new Set<SidebarTabId>(SIDEBAR_TAB_IDS);
const isSidebarTabId = (id: string): id is SidebarTabId => sidebarTabIds.has(id as SidebarTabId);

const sidebarTabConfig = (item: Item, t: TFunction, customFieldDefinitions?: CustomFieldDTO[]) => ({
  system: {
    icon: <Settings />,
    tooltip: t(($) => $.adaptivePlanner.itemPanel.systemFields),
    content: <SystemFields />,
  },
  customFields: {
    icon: <BuildCircle />,
    tooltip: t(($) => $.adaptivePlanner.itemPanel.customFields.label),
    content: <CustomFields item={item} customFieldDefinitions={customFieldDefinitions} />,
  },
  planScheduling: {
    icon: <EventAvailable />,
    tooltip: t(($) => $.adaptivePlanner.itemPanel.planScheduling.label),
    content: <PlanScheduling item={item} />,
  },
});

export const buildSidebarTabs = (
  item: Item,
  t: TFunction,
  tabs: TabConfig[],
  customFieldDefinitions?: CustomFieldDTO[],
) => {
  const config = sidebarTabConfig(item, t, customFieldDefinitions);
  return tabs
    .filter((tab): tab is TabConfig & { id: SidebarTabId } => tab.visible && isSidebarTabId(tab.id))
    .map((tab) => {
      const tabConfig = config[tab.id];
      return {
        id: tab.id,
        adapter: tab.adapter,
        icon: tabConfig.icon,
        tooltip: tabConfig.tooltip,
        content: tab.content ?? tabConfig.content,
      };
    });
};

const toMainTab = (panelTab: TabValueType): string =>
  panelTab === 'dependencies' || panelTab === 'syncFailures' || panelTab === 'financials'
    ? panelTab
    : 'itemDetails';

const MAIN_TAB_TO_TAB_VALUE: Record<string, TabValueType> = {
  dependencies: TabValue.DEPENDENCIES,
  syncFailures: TabValue.SYNC_FAILURES,
  financials: TabValue.FINANCIALS,
};

export const resolveTabValue = (mainTab: string, sidebarTab: SidebarTabId): TabValueType =>
  MAIN_TAB_TO_TAB_VALUE[mainTab] ?? (sidebarTab as TabValueType);

export const computeNavItemClick = ({
  tabId,
  sidebarTab,
  expanded,
  shouldDisplayRightSubPanelAsMainPanel,
}: {
  tabId: SidebarTabId;
  sidebarTab: SidebarTabId;
  expanded: boolean;
  shouldDisplayRightSubPanelAsMainPanel: boolean;
}): { sidebarTab: SidebarTabId; shouldDisplayRightSubPanelAsMainPanel: boolean } => {
  if (!expanded) {
    if (sidebarTab === tabId && shouldDisplayRightSubPanelAsMainPanel) {
      return { sidebarTab, shouldDisplayRightSubPanelAsMainPanel: false };
    }
    return { sidebarTab: tabId, shouldDisplayRightSubPanelAsMainPanel: true };
  }
  return { sidebarTab: tabId, shouldDisplayRightSubPanelAsMainPanel };
};

const toSidebarTab = (panelTab: TabValueType): SidebarTabId =>
  panelTab === 'system' || panelTab === 'customFields' || panelTab === 'planScheduling'
    ? panelTab
    : 'planScheduling';

export const computeInitialActiveTab = (
  expanded: boolean,
  panelTab: TabValueType,
  hasSidebarTab: boolean,
): string => (!expanded && hasSidebarTab ? 'sidebarView' : toMainTab(panelTab));

export const computeInitialSidebarAsMain = (expanded: boolean, hasSidebarTab: boolean): boolean =>
  !expanded && hasSidebarTab;

interface ItemDetailsProps {
  item: Item;
  tabs: TabConfig[];
  panelTab: TabValueType;
  expanded: boolean;
  onTabChange?: (tab: TabValueType) => void;
  customFieldDefinitions?: CustomFieldDTO[];
}

export const ItemDetails = ({
  item,
  tabs,
  panelTab,
  expanded,
  onTabChange,
  customFieldDefinitions,
}: ItemDetailsProps) => {
  const { t } = useTranslation();
  const [sidebarTab, setSidebarTab] = useState(() => toSidebarTab(panelTab));
  const sidebarTabs = buildSidebarTabs(item, t, tabs, customFieldDefinitions);
  const hasSidebar = sidebarTabs.length > 0;
  const activeSidebarTab = sidebarTabs.find((tab) => tab.id === sidebarTab);
  const hasSidebarTab = isSidebarTabId(panelTab) && sidebarTabs.some((tab) => tab.id === panelTab);
  const [activeTab, setActiveTab] = useState(() =>
    computeInitialActiveTab(expanded, panelTab, hasSidebarTab),
  );
  const [shouldDisplayRightSubPanelAsMainPanel, setShouldDisplayRightSubPanelAsMainPanel] =
    useState(() => computeInitialSidebarAsMain(expanded, hasSidebarTab));
  const dependenciesTab = tabs.find((tab) => tab.id === 'dependencies');
  const financialsTab = tabs.find((tab) => tab.id === 'financials');

  const adapter = useTabAdapter<ItemDetailsTabAdapter>();
  const isPublishedContext = useIsPublishedContext();
  const itemTypeId = adapter.getFieldValue('itemTypeId');
  const itemStatusId = adapter.getFieldValue('itemStatusId');
  const description = adapter.getFieldValue('description');
  const descriptionTruncationLink =
    item.externalLink != null && (description?.length ?? 0) >= INTEGRATION_DESCRIPTION_MAX_LENGTH
      ? item.externalLink
      : null;
  const [descriptionEditorKey, setDescriptionEditorKey] = useState(0);
  const descriptionRef = useRef(description);
  descriptionRef.current = description;
  const itemStatusRequestIdRef = useRef(0);
  const itemTypeRequestIdRef = useRef(0);
  const [optimisticFieldValues, setOptimisticFieldValues] = useState<OptimisticFieldValues>({
    itemStatusId,
    itemTypeId,
  });

  const [fieldStates, setFieldStates] = useState<Record<FieldName, FieldState>>({
    description: { isPending: false, isSuccess: false, isError: false, error: null },
    itemStatusId: { isPending: false, isSuccess: false, isError: false, error: null },
    parentId: { isPending: false, isSuccess: false, isError: false, error: null },
    itemTypeId: { isPending: false, isSuccess: false, isError: false, error: null },
  });

  const setFieldState = useCallback((field: FieldName, partial: Partial<FieldState>) => {
    setFieldStates((prev) => ({
      ...prev,
      [field]: { ...prev[field], ...partial },
    }));
  }, []);

  const setOptimisticFieldValue = useCallback(
    <K extends keyof OptimisticFieldValues>(field: K, value: OptimisticFieldValues[K]) => {
      setOptimisticFieldValues((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [],
  );

  const clearFieldSuccess = (field: FieldName) => {
    setFieldStates((prev) => ({
      ...prev,
      [field]: { ...prev[field], isSuccess: false },
    }));
  };

  useEffect(() => {
    if (expanded && activeTab === 'sidebarView') {
      setActiveTab('itemDetails');
      setShouldDisplayRightSubPanelAsMainPanel(false);
    }
  }, [expanded, activeTab]);

  useEffect(() => {
    setOptimisticFieldValues((prev) => ({
      ...prev,
      itemStatusId: syncOptimisticFieldValue(
        prev.itemStatusId,
        itemStatusId,
        fieldStates.itemStatusId,
      ),
    }));
  }, [fieldStates.itemStatusId, itemStatusId]);

  useEffect(() => {
    setOptimisticFieldValues((prev) => ({
      ...prev,
      itemTypeId: syncOptimisticFieldValue(prev.itemTypeId, itemTypeId, fieldStates.itemTypeId),
    }));
  }, [fieldStates.itemTypeId, itemTypeId]);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    (Object.entries(fieldStates) as [FieldName, FieldState][]).forEach(([field, state]) => {
      if (state.isSuccess) {
        const timer = setTimeout(() => clearFieldSuccess(field), 2000);
        timers.push(timer);
      }
    });

    return () => timers.forEach(clearTimeout);
    // biome-ignore lint/correctness/useExhaustiveDependencies: dependency array is intentionally limited to prevent excessive re-renders
  }, [fieldStates, clearFieldSuccess]);

  const { itemTypes, itemStatuses, parentItem } = useItemPanelQueryWithPublished(item);
  const { data: itemTypeLevels = EMPTY_ITEM_TYPE_LEVELS } = useItemTypeLevelsQueryWithPublished();
  const { data: integrationIdsByItemId } = useGetWorkIntegrationIdsByItemIds(
    item.externalKey && !isPublishedContext ? [item.id] : [],
  );
  const { data: integrationsById } = useGetAllWorkIntegrationsById({
    enabled: !isPublishedContext,
  });
  const integrationId = integrationIdsByItemId?.[item.id];
  const integration = integrationId ? integrationsById?.[integrationId] : undefined;
  const sourceSystem = integration?.sourceSystemType;
  const { data: itemSyncFailures } = useGetItemSyncFailedItems({
    itemIdOrSourceItemKey: !isPublishedContext ? String(item.id) : undefined,
    page: 0,
    pageSize: 20,
  });
  const syncFailureCount = itemSyncFailures?.totalCount ?? 0;
  const parentType = itemTypes.find((t) => t.id === parentItem?.itemTypeId);
  const itemTypeOptions = useMemo(
    () =>
      sortItemTypesByLevel(itemTypes, itemTypeLevels).map((t) => ({
        value: t.id,
        label: t.name,
        icon: <ItemTypeIcon itemType={t} />,
      })),
    [itemTypes, itemTypeLevels],
  );

  const statusOptions = itemStatuses.map((s) => ({
    value: s.id,
    label: s.name,
    color: s.color,
  }));

  const parentOption = parentItem
    ? {
        value: parentItem.id,
        label: parentItem.name,
        icon: parentType ? <ItemTypeIcon itemType={parentType} /> : null,
      }
    : undefined;
  const parentItemId = parentItem?.id;
  const parentItemName = parentItem?.name;
  const parentItemTypeId = parentItem?.itemTypeId;
  const parentAutocompleteValueFromQuery = useMemo<ParentItemOption | null>(
    () =>
      parentItemId != null && parentItemName != null && parentItemTypeId != null
        ? {
            id: parentItemId,
            name: parentItemName,
            itemTypeId: parentItemTypeId,
          }
        : null,
    [parentItemId, parentItemName, parentItemTypeId],
  );
  const [parentSelectionOverride, setParentSelectionOverride] = useState<
    ParentItemOption | null | undefined
  >(undefined);
  const parentAutocompleteValue =
    parentSelectionOverride === undefined
      ? parentAutocompleteValueFromQuery
      : parentSelectionOverride;

  const doesParentSelectionMatchQuery = useCallback(
    (overrideValue: ParentItemOption | null | undefined, queryValue: ParentItemOption | null) => {
      if (overrideValue === undefined) return false;
      if (overrideValue === null || queryValue === null) {
        return overrideValue === queryValue;
      }

      return (
        overrideValue.id === queryValue.id &&
        overrideValue.name === queryValue.name &&
        overrideValue.itemTypeId === queryValue.itemTypeId
      );
    },
    [],
  );

  useEffect(() => {
    if (doesParentSelectionMatchQuery(parentSelectionOverride, parentAutocompleteValueFromQuery)) {
      setParentSelectionOverride(undefined);
    }
  }, [doesParentSelectionMatchQuery, parentAutocompleteValueFromQuery, parentSelectionOverride]);
  const { showParentField, validParentLevel } = useMemo(() => {
    const selectedItemType = itemTypes.find((it) => it.id === itemTypeId);
    if (!selectedItemType) {
      return { showParentField: false, validParentLevel: 0 };
    }

    const selectedItemLevel = itemTypeLevels.find(
      (level) => level.id === selectedItemType.itemLevelId,
    );
    const selectedLevel = selectedItemLevel?.level || 1;
    const hasParentItemType = selectedLevel > 1;

    if (!hasParentItemType) {
      return { showParentField: false, validParentLevel: 0 };
    }

    return { showParentField: true, validParentLevel: selectedLevel - 1 };
  }, [itemTypeId, itemTypeLevels, itemTypes]);

  const selectedItemTypeOption = itemTypeOptions.find(
    (type) => type.value === optimisticFieldValues.itemTypeId,
  );

  const currentItemType = itemTypes.find((t) => t.id === itemTypeId);
  const currentItemLevel = currentItemType
    ? itemTypeLevels.find((l) => l.id === currentItemType.itemLevelId)
    : undefined;

  const filteredItemTypeOptions = useMemo(
    () =>
      currentItemLevel
        ? sortItemTypesByLevel(itemTypes, itemTypeLevels)
            .filter((t) => {
              const level = itemTypeLevels.find((l) => l.id === t.itemLevelId);
              return level?.level === currentItemLevel.level;
            })
            .map((t) => ({ value: t.id, label: t.name, icon: <ItemTypeIcon itemType={t} /> }))
        : [],
    [currentItemLevel, itemTypes, itemTypeLevels],
  );

  const hasItemTypeAlternatives = filteredItemTypeOptions.length > 1;
  const selectedStatusOption = statusOptions.find(
    (opt) => opt.value === optimisticFieldValues.itemStatusId,
  );
  const handleDescriptionSave = useCallback(
    async (value: string) => {
      if (value === (descriptionRef.current ?? '')) return;
      const validation = descriptionSchema.safeParse(value);
      if (!validation.success) {
        setFieldState('description', {
          isPending: false,
          isError: true,
          error: new Error(
            t(($) => $.adaptivePlanner.itemPanel.itemSection.itemDescriptionTooLong, {
              max: DESCRIPTION_MAX_LENGTH,
            }),
          ),
          isSuccess: false,
        });
        return;
      }
      setFieldState('description', {
        isPending: true,
        isError: false,
        error: null,
        isSuccess: false,
      });
      try {
        await adapter.updateFieldValue('description', value);
        setFieldState('description', { isPending: false, isSuccess: true });
      } catch (err) {
        setFieldState('description', {
          isPending: false,
          isError: true,
          error: err instanceof Error ? err : new Error('Failed to update description'),
        });
        setDescriptionEditorKey((previousKey) => previousKey + 1);
      }
    },
    [adapter, setFieldState, t],
  );

  const handleStatusChange = async (value: number | undefined) => {
    if (
      value === undefined ||
      value === optimisticFieldValues.itemStatusId ||
      fieldStates.itemStatusId.isPending
    ) {
      return;
    }

    const previousValue = optimisticFieldValues.itemStatusId;
    const requestId = ++itemStatusRequestIdRef.current;
    setOptimisticFieldValue('itemStatusId', value);
    try {
      setFieldState('itemStatusId', {
        isPending: true,
        isError: false,
        error: null,
        isSuccess: false,
      });
      await adapter.updateFieldValue('itemStatusId', value);
      if (requestId !== itemStatusRequestIdRef.current) {
        return;
      }
      setFieldState('itemStatusId', { isPending: false, isSuccess: true });
    } catch (err) {
      if (requestId !== itemStatusRequestIdRef.current) {
        return;
      }
      setOptimisticFieldValue('itemStatusId', previousValue);
      setFieldState('itemStatusId', {
        isPending: false,
        isError: true,
        error: err instanceof Error ? err : new Error('Failed to update status'),
      });
    }
  };

  const handleItemTypeChange = async (value: number | undefined) => {
    if (
      value === undefined ||
      value === optimisticFieldValues.itemTypeId ||
      fieldStates.itemTypeId.isPending
    ) {
      return;
    }

    const previousValue = optimisticFieldValues.itemTypeId;
    const requestId = ++itemTypeRequestIdRef.current;
    setOptimisticFieldValue('itemTypeId', value);
    try {
      setFieldState('itemTypeId', {
        isPending: true,
        isError: false,
        error: null,
        isSuccess: false,
      });
      await adapter.updateFieldValue('itemTypeId', value);
      if (requestId !== itemTypeRequestIdRef.current) {
        return;
      }
      setFieldState('itemTypeId', { isPending: false, isSuccess: true });
    } catch (err) {
      if (requestId !== itemTypeRequestIdRef.current) {
        return;
      }
      setOptimisticFieldValue('itemTypeId', previousValue);
      setFieldState('itemTypeId', {
        isPending: false,
        isError: true,
        error: err instanceof Error ? err : new Error('Failed to update item type'),
      });
    }
  };

  const handleParentChange = async (nextParent: ParentItemOption | null) => {
    setParentSelectionOverride(nextParent);
    try {
      setFieldState('parentId', { isPending: true, isError: false, error: null, isSuccess: false });
      await adapter.updateFieldValue('parentId', nextParent?.id ?? null);
      setFieldState('parentId', { isPending: false, isSuccess: true });
    } catch (err) {
      setParentSelectionOverride(undefined);
      setFieldState('parentId', {
        isPending: false,
        isError: true,
        error: err instanceof Error ? err : new Error('Failed to update parent'),
      });
    }
  };

  const tabTriggerClassName =
    'flex-initial flex items-center rounded-none border-b-2 border-transparent px-2 py-2 text-sm font-normal text-current cursor-pointer shadow-none data-[state=active]:border-b-black data-[state=active]:bg-transparent data-[state=active]:shadow-none';

  const handleMainTabChange = (tab: string) => {
    setActiveTab(tab);
    setShouldDisplayRightSubPanelAsMainPanel(false);
    onTabChange?.(resolveTabValue(tab, sidebarTab));
  };

  const handleNavItemClick = (tabId: SidebarTabId) => {
    const next = computeNavItemClick({
      tabId,
      sidebarTab,
      expanded,
      shouldDisplayRightSubPanelAsMainPanel,
    });
    setSidebarTab(next.sidebarTab);
    setShouldDisplayRightSubPanelAsMainPanel(next.shouldDisplayRightSubPanelAsMainPanel);
    if (!expanded) {
      setActiveTab(next.shouldDisplayRightSubPanelAsMainPanel ? 'sidebarView' : 'itemDetails');
    }
    onTabChange?.(resolveTabValue(activeTab, next.sidebarTab));
  };

  const sidebarNavigation = (
    <div className="tempo-nav-reset tempo-sidebar collapsed">
      <PrimaryNavigationSection>
        {sidebarTabs.map((tab) => (
          <NavigationItem
            key={tab.id}
            icon={tab.icon}
            active={sidebarTab === tab.id && (expanded || shouldDisplayRightSubPanelAsMainPanel)}
            onClick={() => handleNavItemClick(tab.id)}
            testId={`item-panel-${tab.id}-tab`}
          >
            {tab.tooltip}
          </NavigationItem>
        ))}
      </PrimaryNavigationSection>
    </div>
  );

  return (
    <Tabs
      value={activeTab}
      onValueChange={handleMainTabChange}
      className="flex h-full flex-col gap-0"
    >
      <TabsList className="h-auto w-full justify-start rounded-none border-b border-border-neutral bg-transparent p-0">
        <TabsTrigger value="itemDetails" className={tabTriggerClassName}>
          <EditNote />
          {t(($) => $.adaptivePlanner.itemPanel.itemSection.itemDetails)}
        </TabsTrigger>
        <TabsTrigger value="dependencies" className={tabTriggerClassName}>
          <Dependency />
          {t(($) => $.adaptivePlanner.itemPanel.dependencies)}
        </TabsTrigger>
        {financialsTab?.visible && (
          <TabsTrigger value="financials" className={tabTriggerClassName}>
            <AccountBalance />
            {t(($) => $.adaptivePlanner.itemPanel.financials)}
          </TabsTrigger>
        )}
        {syncFailureCount > 0 && (
          <TabsTrigger
            value="syncFailures"
            className={tabTriggerClassName}
            data-testid="tab-trigger-syncFailures"
          >
            <SyncProblem />
            {t(($) => $.common.settings['work-integrations'].detail.syncFailures.title)}
            {syncFailureCount > 0 && (
              <span className="ml-1 text-xs text-text-neutral-secondary">({syncFailureCount})</span>
            )}
          </TabsTrigger>
        )}
      </TabsList>
      <TabsContent value="itemDetails" className="flex-1 mt-0 overflow-hidden">
        <div className="flex h-full flex-row">
          <div className="flex flex-1 flex-col overflow-y-auto p-6">
            <div className="flex flex-1 flex-col gap-4">
              <div className="grid w-full grid-cols-2 gap-4">
                <FieldWithStatusIndicator
                  inputId="item-type"
                  name={t(($) => $.adaptivePlanner.itemPanel.itemSection.itemType)}
                  isPending={fieldStates.itemTypeId.isPending}
                  isSuccess={fieldStates.itemTypeId.isSuccess}
                >
                  {adapter.isEditable('itemTypeId') && hasItemTypeAlternatives ? (
                    <div className="flex flex-col gap-1">
                      <Select
                        id="item-type"
                        name="type"
                        value={selectedItemTypeOption}
                        onChange={(opt) => handleItemTypeChange(opt?.value)}
                        options={filteredItemTypeOptions}
                        formatOptionLabel={(option) => <OptionWithIcon option={option} />}
                        getOptionValue={(option) => String(option.value)}
                        getOptionLabel={(option) => option.label}
                        placeholder={t(($) => $.adaptivePlanner.itemPanel.selectPlaceholders.type)}
                        isDisabled={
                          adapter.isDisabled('itemTypeId') || fieldStates.itemTypeId.isPending
                        }
                        menuPosition="fixed"
                        styles={ABOVE_RICH_TEXT_EDITOR_MENU_STYLES}
                      />
                      {fieldStates.itemTypeId.isError && fieldStates.itemTypeId.error && (
                        <span role="alert" aria-live="polite" className="text-xs text-text-error">
                          {fieldStates.itemTypeId.error.message}
                        </span>
                      )}
                    </div>
                  ) : selectedItemTypeOption ? (
                    <div className="flex items-center gap-2 text-sm text-text-neutral-primary">
                      {selectedItemTypeOption.icon}
                      <span>{selectedItemTypeOption.label}</span>
                    </div>
                  ) : (
                    <EmptyFieldValue />
                  )}
                </FieldWithStatusIndicator>
                <FieldWithStatusIndicator
                  inputId="item-status"
                  name={t(($) => $.adaptivePlanner.itemPanel.itemSection.itemStatus)}
                  isPending={fieldStates.itemStatusId.isPending}
                  isSuccess={fieldStates.itemStatusId.isSuccess}
                >
                  {adapter.isEditable('itemStatusId') ? (
                    <div className="flex flex-col gap-1">
                      <Select
                        id="item-status"
                        name="status"
                        value={selectedStatusOption}
                        onChange={(opt) => handleStatusChange(opt?.value)}
                        options={statusOptions}
                        formatOptionLabel={(option) => (
                          <ItemStatusBadge color={option.color ?? ''} label={option.label} />
                        )}
                        getOptionValue={(option) => String(option.value)}
                        getOptionLabel={(option) => option.label}
                        placeholder={t(
                          ($) => $.adaptivePlanner.itemPanel.selectPlaceholders.status,
                        )}
                        isDisabled={
                          adapter.isDisabled('itemStatusId') || fieldStates.itemStatusId.isPending
                        }
                        menuPosition="fixed"
                        styles={ABOVE_RICH_TEXT_EDITOR_MENU_STYLES}
                      />
                      {fieldStates.itemStatusId.isError && fieldStates.itemStatusId.error && (
                        <span className="text-xs text-text-error">
                          {fieldStates.itemStatusId.error.message}
                        </span>
                      )}
                    </div>
                  ) : selectedStatusOption ? (
                    <ItemStatusBadge
                      color={selectedStatusOption.color ?? ''}
                      label={selectedStatusOption.label}
                    />
                  ) : (
                    <EmptyFieldValue />
                  )}
                </FieldWithStatusIndicator>
                {adapter.isVisible('parentId') && showParentField && (
                  <FieldWithStatusIndicator
                    inputId="item-parent"
                    name={t(($) => $.adaptivePlanner.itemPanel.itemSection.parent)}
                    isPending={fieldStates.parentId.isPending}
                    isSuccess={fieldStates.parentId.isSuccess}
                  >
                    {adapter.isEditable('parentId') ? (
                      <div data-testid="item-panel-parent-field" className="flex flex-col gap-1">
                        <ParentItemAutocomplete
                          value={parentAutocompleteValue}
                          onChange={handleParentChange}
                          validParentLevel={validParentLevel}
                          loopId={item.loopId}
                          excludedItemIds={[item.id]}
                          disabled={
                            adapter.isDisabled('parentId') || fieldStates.parentId.isPending
                          }
                          isClearable
                        />
                        {fieldStates.parentId.isError && fieldStates.parentId.error && (
                          <span className="text-xs text-text-error">
                            {fieldStates.parentId.error.message}
                          </span>
                        )}
                      </div>
                    ) : parentOption ? (
                      <div
                        data-testid="item-panel-parent-field"
                        className="flex items-center gap-2 text-sm text-text-neutral-primary"
                      >
                        {parentOption.icon}
                        <span>{parentOption.label}</span>
                      </div>
                    ) : (
                      <EmptyFieldValue data-testid="item-panel-parent-field" />
                    )}
                  </FieldWithStatusIndicator>
                )}
                <FieldWithStatusIndicator
                  inputId="item-integration"
                  name={t(($) => $.adaptivePlanner.itemPanel.integration.label)}
                >
                  <IntegrationField
                    itemId={item.id}
                    loopId={item.loopId}
                    itemTypeId={item.itemTypeId}
                  />
                </FieldWithStatusIndicator>
              </div>
              {item.externalKey && (
                <ExternalKeyCard
                  itemId={item.id}
                  externalKey={item.externalKey}
                  externalLink={item.externalLink}
                  integration={integration}
                  sourceSystem={sourceSystem}
                />
              )}
              <DescriptionField
                isEditable={adapter.isEditable('description')}
                isDisabled={adapter.isDisabled('description')}
                description={description}
                truncationLink={descriptionTruncationLink}
                fieldState={fieldStates.description}
                itemId={item.id}
                editorKey={descriptionEditorKey}
                sourceSystem={sourceSystem}
                onSave={handleDescriptionSave}
              />
            </div>
          </div>
          {hasSidebar && (
            <div className="flex h-full shrink-0 flex-row gap-0">
              {expanded && (
                <div className="w-96 border-r border-border-neutral bg-bg-surface-foundation p-6 overflow-y-auto">
                  {sidebarTabs.map(
                    (tab) =>
                      sidebarTab === tab.id && (
                        <TabAdapterProvider key={tab.id} adapter={tab.adapter}>
                          {tab.content}
                        </TabAdapterProvider>
                      ),
                  )}
                </div>
              )}
              {sidebarNavigation}
            </div>
          )}
        </div>
      </TabsContent>
      <TabsContent value="sidebarView" className="flex-1 mt-0 overflow-hidden">
        <div className="flex h-full flex-row">
          <div className="flex-1 overflow-y-auto p-6">
            {activeSidebarTab && (
              <TabAdapterProvider key={activeSidebarTab.id} adapter={activeSidebarTab.adapter}>
                {activeSidebarTab.content}
              </TabAdapterProvider>
            )}
          </div>
          {hasSidebar && (
            <div className="flex h-full shrink-0 flex-row gap-0">{sidebarNavigation}</div>
          )}
        </div>
      </TabsContent>
      <TabsContent value="dependencies" className="flex-1 mt-0 overflow-y-auto p-4">
        {dependenciesTab &&
          (dependenciesTab.content ? (
            <TabAdapterProvider adapter={dependenciesTab.adapter}>
              {dependenciesTab.content}
            </TabAdapterProvider>
          ) : (
            <TabAdapterProvider adapter={dependenciesTab.adapter}>
              <Dependencies item={item} />
            </TabAdapterProvider>
          ))}
      </TabsContent>
      <TabsContent value="financials" className="flex-1 mt-0 overflow-y-auto p-4">
        {financialsTab?.visible && (
          <TabAdapterProvider adapter={financialsTab.adapter}>
            <Financials />
          </TabAdapterProvider>
        )}
      </TabsContent>
      {syncFailureCount > 0 && (
        <TabsContent value="syncFailures" className="flex-1 mt-0 overflow-y-auto p-4">
          {(item.externalKey || syncFailureCount > 0) && (
            <div
              className="mb-4 flex items-center justify-between"
              data-testid="sync-failures-header"
            >
              {item.externalKey ? (
                <span
                  className="flex items-center gap-1.5 text-xs text-text-neutral-secondary"
                  data-testid="sync-failures-item-key"
                >
                  <span className="font-medium">
                    {t(($) => $.adaptivePlanner.itemPanel.systemField.externalKey)}:
                  </span>
                  <span className="font-mono">{item.externalKey}</span>
                </span>
              ) : (
                <span />
              )}
              {syncFailureCount > 0 && (
                <span
                  className="rounded-full bg-bg-danger-subtle px-2.5 py-0.5 text-xs font-medium text-text-danger"
                  data-testid="sync-failures-count"
                >
                  {t(
                    ($) => $.common.settings['work-integrations'].detail.syncFailures.failureCount,
                    { count: syncFailureCount },
                  )}
                </span>
              )}
            </div>
          )}
          <ItemSyncFailures itemId={item.id} />
        </TabsContent>
      )}
    </Tabs>
  );
};
